import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

export interface TimelinePhaseProgress {
  id: string; // timeline_phase_id
  title: string;
  sortOrder: number;
  status: "Pending" | "Submitted" | "Approved" | "Rejected" | "Hold";
  submittedAt: string | null;
  /** Saved separately before photo submission. Immutable once set. */
  actualStartDate: string | null;
}

export interface PortalBeneficiary {
  applicationId: string;
  beneficiaryName: string;
  district: string;
  mandal: string;
  village: string;
  plotLatitude?: number | null;
  plotLongitude?: number | null;
  timeline: TimelinePhaseProgress[];
  activePhaseId: string | null;
}

export interface SubcontractorPortalData {
  subcontractorName: string;
  beneficiaries: PortalBeneficiary[];
}

export interface TimelineApprovalRecord {
  id: string;
  application_id: string;
  timeline_phase_id: string;
  photo_data: string;
  photo_data_2: string | null;
  photo_data_3: string | null;
  video_data: string | null;
  actual_start_date: string | null;
  status: "Submitted" | "Approved" | "Rejected" | "Hold";
  status_notes: string | null;
  created_at: string;
  updated_at: string;
  beneficiary_name: string;
  district: string;
  mandal: string;
  village: string;
  subcontractor_name: string;
  phase_title: string;
}

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string };
    if (j.error) return j.error;
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export async function fetchSubcontractorPortalData(token: string): Promise<SubcontractorPortalData> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/subcontractor-portal/${encodeURIComponent(token)}/beneficiaries`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<SubcontractorPortalData>;
}

/** Save the actual start date for a phase. Immutable — can only be set once. */
export async function savePhaseStartDate(
  token: string,
  applicationId: string,
  timelinePhaseId: string,
  actualStartDate: string, // "YYYY-MM-DD"
): Promise<{ success: boolean; actualStartDate: string }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/subcontractor-portal/${encodeURIComponent(token)}/set-phase-start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ applicationId, timelinePhaseId, actualStartDate }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ success: boolean; actualStartDate: string }>;
}

export async function submitTimelinePhasePhoto(
  token: string,
  applicationId: string,
  timelinePhaseId: string,
  photoData: string,
  opts?: {
    photoData2?: string;
    photoData3?: string;
    videoData?: string;
  }
): Promise<{ success: boolean; id: string }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/subcontractor-portal/${encodeURIComponent(token)}/submit-phase`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      applicationId,
      timelinePhaseId,
      photoData,
      photoData2: opts?.photoData2,
      photoData3: opts?.photoData3,
      videoData: opts?.videoData,
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ success: boolean; id: string }>;
}

export async function fetchTimelineApprovals(status: string = "Submitted"): Promise<TimelineApprovalRecord[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/timeline-approvals?status=${encodeURIComponent(status)}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<TimelineApprovalRecord[]>;
}

export interface TimelineApprovalCounts {
  Submitted: number;
  Approved: number;
  Rejected: number;
  Hold: number;
}

export async function fetchTimelineApprovalCounts(): Promise<TimelineApprovalCounts> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/timeline-approvals/counts`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<TimelineApprovalCounts>;
}

export async function updateTimelineApprovalStatus(
  id: string,
  status: "Approved" | "Rejected" | "Hold",
  notes?: string
): Promise<{ success: boolean }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/timeline-approvals/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, notes }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ success: boolean }>;
}

/** Per-phase progress for one beneficiary — includes submission status & photo */
export interface BeneficiaryPhaseProgress {
  timeline_phase_id: string;
  phase_title: string;
  sort_order: number;
  status: "Pending" | "Submitted" | "Approved" | "Rejected" | "Hold";
  photo_data: string | null;
  photo_data_2: string | null;
  photo_data_3: string | null;
  video_data: string | null;
  actual_start_date: string | null;
  submitted_at: string | null;
  updated_at: string | null;
}

/** Fetch the subcontractor-submitted timeline progress for one beneficiary */
export async function fetchBeneficiaryTimelineProgress(
  applicationId: string,
): Promise<BeneficiaryPhaseProgress[]> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/timeline-progress/${encodeURIComponent(applicationId)}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<BeneficiaryPhaseProgress[]>;
}
